import React, { useContext, useEffect, useRef } from 'react';
import classnames from 'classnames';
import IconButton from 'components/IconButton';
import { useCombinedRefs } from 'common';
import { CONSTANTS } from 'common/ListKeyboardNav';
import { HorizontalListItemContext } from './HorizontalListItem';

const { LIST_ITEM_TABABLE_ITEM } = CONSTANTS;

export interface HorizontalListButtonProps extends React.ComponentPropsWithRef<'button'> {
  /**  children, Icon Component to place in the List */
  children: React.ReactNode;
  /** Display the list item as disabled. */
  disabled?: boolean;
  /** @ignore */
  onKeyDown?: React.KeyboardEventHandler;
}

/** All other props will be provided to IconButton. */
const HorizontalListButton = React.forwardRef<HTMLElement, HorizontalListButtonProps>((props: HorizontalListButtonProps, ref) => {
  const {
    children,
    disabled,
    onKeyDown,
    ...otherProps
  } = props;
  const horizontalListItemContext = useContext(HorizontalListItemContext);
  const buttonRef = useRef(null);
  const combinedRef = useCombinedRefs(ref, buttonRef);

  const isDisabled = disabled || horizontalListItemContext.disabled;

  useEffect(() => {
    horizontalListItemContext.autoFocus && !disabled && buttonRef.current && buttonRef.current.focus();
  }, [horizontalListItemContext.autoFocus]);

  const handleKeyDown = (event) => {
    onKeyDown && onKeyDown(event);
    horizontalListItemContext.onKeyDown && horizontalListItemContext.onKeyDown(event);
  };

  const classes = classnames({
    [LIST_ITEM_TABABLE_ITEM]: !isDisabled
  });

  return (
    <IconButton
      ref={combinedRef}
      className={classes}
      tabIndex={-1}
      disabled={isDisabled}
      {...otherProps}
      onKeyDown={handleKeyDown}
    >
      {children}
    </IconButton>
  );
});

HorizontalListButton.displayName = 'HorizontalListButton';

HorizontalListButton.defaultProps = {
  disabled: false,
  onKeyDown: undefined
};

export {
  HorizontalListButton as default,
};
